// ===========================================
// Email Unsubscribe (magic link)
// ===========================================
// UNAUTHENTICATED. The token in the URL is the whole credential — the same
// trust model as PublicPermissionSlipPage, and for a stronger reason: the
// primary user here is a login-less contact 2 (`Parent.contact2Email` with no
// `User` row). Requiring a login would exclude exactly the person the feature
// exists for, and an unsubscribe link that demands a password is the classic
// reason people press "spam" instead.
//
// ⚠️ THE PAGE MUST NOT UNSUBSCRIBE ON MOUNT.
//
// Mail scanners (Outlook Safe Links, Proofpoint, Mimecast) and Gmail's image
// proxy fetch every url in an email, usually within seconds of delivery and
// always without a human. The backend enforces this by splitting GET (read) from
// POST (perform) — but a `useEffect` here that fired the mutation on load would
// defeat that split entirely, because a scanner rendering the page would run it.
// The POST happens on a click, and only on a click.
//
// ⚠️ The schoolSlug in the URL is COSMETIC and scopes nothing (verified in
// Phase 3 QA: a school-A token under school-B's slug resolves school A). Every
// tenant fact on this page — name, logo, colours, the masked address — comes
// from the TOKEN. Never pass the slug to an API call as if it were a scope.

import { useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useUnsubscribeDetails, useUnsubscribe } from '../../hooks/useEmailUnsubscribe';

export default function EmailUnsubscribePage() {
  const { token = '' } = useParams<{ token: string }>();

  const { data, isLoading, isError } = useUnsubscribeDetails(token);
  const unsubscribeMutation = useUnsubscribe();

  // Local, because the mutation's own result is the authoritative "it is done"
  // signal and we want it to survive without a refetch.
  const [done, setDone] = useState(false);

  // `mutate`, not `mutateAsync`. `mutateAsync` returns a promise that REJECTS on
  // failure, and an onClick handler has nobody to catch it — which surfaces as
  // an unhandled rejection while the UI still renders the error correctly, so it
  // is invisible in the browser and only shows up in tests. The failure path is
  // reachable here by ordinary means (offline phone, expired token, rate limit),
  // so this is the common case, not an edge one.
  const handleUnsubscribe = () => {
    unsubscribeMutation.mutate(token, { onSuccess: () => setDone(true) });
  };

  // Already opted out before this visit, or opted out just now. Both render the
  // same confirmation — from the parent's point of view there is no difference,
  // and drawing one would only invite a second click.
  const isUnsubscribed = done || data?.alreadyUnsubscribed === true;

  const brand = data?.school?.primaryColor || null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        // A hex with alpha ('#4580E4' + '14') is a ~8% tint, supported
        // everywhere we ship and avoids parsing the colour into rgb().
        bgcolor: brand ? `${brand}14` : 'background.paper',
        borderTop: brand ? `4px solid ${brand}` : undefined,
      }}
    >
      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isError || !data ? (
          // ⚠️ Deliberately vague, and deliberately NOT "this link is invalid".
          // The server returns one uniform 404 for an unknown token, a malformed
          // one and a deleted school, so we genuinely do not know which happened
          // — and a confident wrong statement here would tell someone their
          // unsubscribe failed when the school no longer exists.
          //
          // The fallback instruction matters: this is the page that stops mail,
          // so it must never dead-end. Replying to the email reaches a human.
          <Alert severity="error" icon={<LockIcon />}>
            This link is no longer valid. Please reply to the email you received, or contact your
            school, and they can update your preferences for you.
          </Alert>
        ) : (
          <>
            {/* School identity. This page is unauthenticated and reached from an
                email, so there is no portal shell to say where the parent is.
                The logo and name are what distinguish this from a phishing form
                — worth the vertical space on a page this short. */}
            {data.school.logoUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Box
                  component="img"
                  src={data.school.logoUrl}
                  alt={data.school.name ? `${data.school.name} logo` : 'School logo'}
                  sx={{ maxHeight: 72, maxWidth: '100%', objectFit: 'contain' }}
                />
              </Box>
            )}

            <Card elevation={0} sx={{ bgcolor: 'background.paper' }}>
              <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                {isUnsubscribed ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
                      You're unsubscribed
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ textAlign: 'center', mb: 2 }}
                    >
                      {data.maskedEmail} will no longer receive {data.scopeLabel} from{' '}
                      {data.school.name || 'this school'}.
                    </Typography>

                    {/* ⚠️ This paragraph is a PROMISE THE BACKEND KEEPS, not
                        reassurance copy. `RecipientScope` types transactional
                        mail as the string literal 'TRANSACTIONAL' rather than an
                        enum member, so a row suppressing an invoice or a
                        cancellation is unrepresentable in the database — not
                        merely un-written. Do not soften this into "most emails"
                        unless that stops being true. */}
                    <Alert severity="info" sx={{ mt: 2 }}>
                      You'll still receive important emails about invoices, payments and cancelled
                      classes. Those aren't marketing — you need them.
                    </Alert>
                  </>
                ) : (
                  <>
                    <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
                      Unsubscribe from {data.scopeLabel}?
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ textAlign: 'center', mb: 3 }}
                    >
                      {data.maskedEmail} will stop receiving {data.scopeLabel} from{' '}
                      {data.school.name || 'this school'}.
                    </Typography>

                    {unsubscribeMutation.isError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        We couldn't update your preferences just now. Please try again, or reply to
                        the email you received.
                      </Alert>
                    )}

                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={handleUnsubscribe}
                      disabled={unsubscribeMutation.isPending}
                    >
                      {unsubscribeMutation.isPending ? 'Unsubscribing…' : 'Unsubscribe'}
                    </Button>

                    <Alert severity="info" sx={{ mt: 3 }}>
                      You'll still receive important emails about invoices, payments and cancelled
                      classes.
                    </Alert>
                  </>
                )}
              </CardContent>
            </Card>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 3 }}
            >
              Changed your mind? Sign in to your parent portal to turn these emails back on.
            </Typography>
          </>
        )}
      </Container>
    </Box>
  );
}
